import { create } from 'zustand'
import type { Entity, EntityDiff, EntityMap } from '@/ha/types'

interface EntitiesStore {
  entities: EntityMap
  ready: boolean
  /** apply a decoded diff from the gateway (real HA state always wins) */
  ingest: (diff: EntityDiff) => void
  /** write a predicted state for instant feedback; returns a rollback baseline */
  applyOptimistic: (predicted: Entity) => void
  /** clear a pending flag without changing state (call succeeded, awaiting echo) */
  settle: (id: string) => void
  reset: () => void
}

// rollback baselines for in-flight optimistic updates, keyed by entity_id
const baselines = new Map<string, Entity>()

export const useEntitiesStore = create<EntitiesStore>((set) => ({
  entities: {},
  ready: false,

  ingest: (diff) =>
    set((s) => {
      const next: EntityMap = { ...s.entities }
      for (const id in diff.added) next[id] = diff.added[id]!
      for (const id in diff.changed) {
        // a real update for a pending entity clears the optimistic flag
        next[id] = diff.changed[id]!
        baselines.delete(id)
      }
      for (const id of diff.removed) {
        delete next[id]
        baselines.delete(id)
      }
      return { entities: next, ready: true }
    }),

  applyOptimistic: (predicted) =>
    set((s) => {
      const id = predicted.entity_id
      if (!baselines.has(id) && s.entities[id]) baselines.set(id, s.entities[id]!)
      return { entities: { ...s.entities, [id]: predicted } }
    }),

  settle: (id) =>
    set((s) => {
      const e = s.entities[id]
      if (!e?.pending) return s
      const { pending: _p, pendingSince: _ps, ...rest } = e
      void _p
      void _ps
      return { entities: { ...s.entities, [id]: rest } }
    }),

  reset: () => {
    baselines.clear()
    set({ entities: {}, ready: false })
  },
}))

/** Roll a pending entity back to its baseline if the prediction never confirmed. */
export function rollbackIfStale(id: string, pendingSince: number): boolean {
  const store = useEntitiesStore.getState()
  const cur = store.entities[id]
  if (!cur?.pending || cur.pendingSince !== pendingSince) return false
  const baseline = baselines.get(id)
  baselines.delete(id)
  useEntitiesStore.setState((s) => ({
    entities: { ...s.entities, [id]: baseline ?? { ...cur, pending: false } },
  }))
  return true
}
